import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useRef, useState } from 'react';

export enum AuthSessionVerdict {
  PROBING = 'probing',
  NO_SESSION = 'noSession',
  SESSION_LOST = 'sessionLost'
}

export interface AuthSessionProbeResult {
  verdict: AuthSessionVerdict;
  error: Error | null;
}

const AUTH0_CACHE_KEY_PREFIX = '@@auth0spajs@@';

function hasAuth0CacheKey(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(AUTH0_CACHE_KEY_PREFIX)) return true;
    }
    return false;
  } catch {
    // Safari private mode and some webviews throw SecurityError. Treat as no
    // cache key — the user effectively has no recoverable session in this
    // environment, so the silent-redirect path is the right fallback.
    return false;
  }
}

export function useAuthSessionProbe(): AuthSessionProbeResult {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [verdict, setVerdict] = useState<AuthSessionVerdict>(AuthSessionVerdict.PROBING);
  const [error, setError] = useState<Error | null>(null);
  // useRef, not useState: the guard is only read inside the effect, so flipping
  // it should not trigger a re-render. The ref also stays correct if
  // reactStrictMode is ever turned on (strict-mode double-invoke preserves
  // ref.current across the second pass).
  const probeRanRef = useRef(false);
  // Snapshot so the effect's identity doesn't churn on Auth0Provider re-renders.
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;

  useEffect(() => {
    if (isLoading || isAuthenticated || probeRanRef.current) return;
    probeRanRef.current = true;

    if (!hasAuth0CacheKey()) {
      setVerdict(AuthSessionVerdict.NO_SESSION);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        await getTokenRef.current();
        // success: Auth0Provider's reducer dispatches GET_ACCESS_TOKEN_COMPLETE
        // and flips isAuthenticated. Leave verdict at PROBING — AuthGuard
        // renders children once the SDK state propagates.
      } catch (e) {
        if (cancelled) return;
        const oauthCode = (e as { error?: string })?.error;
        if (oauthCode === 'missing_refresh_token') {
          setVerdict(AuthSessionVerdict.NO_SESSION);
        } else {
          setVerdict(AuthSessionVerdict.SESSION_LOST);
        }
        setError(e as Error);
        if (oauthCode !== 'missing_refresh_token' && oauthCode !== 'login_required') {
          // Sanitize: send a constant message + the OAuth code as a tag.
          // The raw error may contain `error_description` with PII (email, user id).
          Sentry.captureException(new Error('auth_probe_failed'), {
            tags: { oauthCode: oauthCode ?? 'unknown' }
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading]);

  return { verdict, error };
}
